import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';

import { partitionTranscriptByScene } from './transcript-partition';

import type { RootState } from 'js/src/libs/RootState';

/**
 * Default length of the short, git-style scene handle shown to the agent. A scene id is a UUID, so
 * six hex characters is enough to disambiguate every scene in a realistic video; the map builder
 * below lengthens a prefix only when two ids would otherwise collide.
 */
export const SHORT_SCENE_ID_LENGTH = 6;

/** Map each id to a short, git-style prefix (>= minLength chars), lengthened only enough to stay unique. */
export function computeShortIds(ids: string[], minLength: number): Map<string, string> {
    const shortIds = new Map<string, string>();
    for (const id of ids) {
        let length = minLength;
        while (
            length < id.length &&
            ids.some((other) => other !== id && other.startsWith(id.slice(0, length)))
        ) {
            length++;
        }
        shortIds.set(id, id.slice(0, length));
    }
    return shortIds;
}

/** The default (collision-agnostic) short handle for a single id, for display where the full id set isn't at hand. */
export function defaultShortId(id: string, minLength: number): string {
    return id.slice(0, minLength);
}

/** Map each scene id to its short, git-style handle. See {@link computeShortIds}. */
export function computeSceneShortIds(sceneIds: string[]): Map<string, string> {
    return computeShortIds(sceneIds, SHORT_SCENE_ID_LENGTH);
}

/** The default (collision-agnostic) short handle for a single scene id, where the full scene set isn't at hand. */
export function defaultShortSceneId(sceneId: string): string {
    return defaultShortId(sceneId, SHORT_SCENE_ID_LENGTH);
}

/** The short scene_id handle to show in tool messages — matches the video state, collision-safe. */
export function sceneDisplayId(luminary: Luminary, sceneId: string): string {
    return (
        computeSceneShortIds(luminary.scenes.map((scene) => scene.id)).get(sceneId) ??
        defaultShortSceneId(sceneId)
    );
}

/**
 * The transcript words a voice-driven-group scene owns, joined into a string, or null when the
 * scene has no transcript (text-on-media, etc.). Reads the shared transcript partition so this
 * matches the `covers:` line the agent sees and what add_voiceover_scene reports by construction.
 */
export function getSceneCoversWords(state: RootState, scene: Scene): string | null {
    const sceneGroup = scene.sceneGroup;
    if (!sceneGroup) {
        return null;
    }
    const partition = partitionTranscriptByScene(state, sceneGroup);
    const entry = partition?.find((part) => part.scene.id === scene.id);
    return entry ? entry.words.join(' ') : null;
}

export type SceneByIdResolution = { scene: Scene; sceneIndex: number } | { error: string };

/**
 * Resolve a scene by a short or full scene id against the live scene list, fail-closed: reject on
 * zero matches (a bad/mistyped id must be a loud error, never a wrong edit) and on an ambiguous
 * prefix (list the candidate scenes with their coverage so the agent can pick a longer id).
 */
export function resolveSceneById(
    luminary: Luminary,
    state: RootState,
    sceneId: string,
): SceneByIdResolution {
    const normalized = sceneId.trim().toLowerCase();
    const matches = luminary.scenes
        .map((scene, sceneIndex) => ({ scene, sceneIndex }))
        .filter(({ scene }) => scene.id.toLowerCase().startsWith(normalized));

    if (matches.length === 0) {
        return {
            error: `No scene found with scene_id "${sceneId}". Re-read the current video state and use a scene_id exactly as shown there.`,
        };
    }
    if (matches.length > 1) {
        const shortIds = computeSceneShortIds(luminary.scenes.map((scene) => scene.id));
        const candidates = matches
            .map(({ scene }) => {
                const covers = getSceneCoversWords(state, scene);
                const coversNote = covers !== null ? ` covers "${covers}"` : '';
                return `  - scene_id ${shortIds.get(scene.id)}${coversNote}`;
            })
            .join('\n');
        return {
            error: `scene_id "${sceneId}" is ambiguous — it matches ${matches.length} scenes:\n${candidates}\nPass a longer scene_id to pick one.`,
        };
    }
    return matches[0];
}

export interface SceneHandleArgs {
    scene_id?: string;
}

/**
 * Thrown when a tool can't resolve the scene it was asked to act on — a missing, unknown, or
 * ambiguous `scene_id`. The message is written for the model; a tool's `catch` (or the base tool
 * wrapper) surfaces it directly as the tool result.
 */
export class SceneResolutionError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SceneResolutionError';
    }
}

/**
 * Resolve a scene by its stable `scene_id` (the model-facing handle). Throws {@link
 * SceneResolutionError} on a missing id and on a bad/ambiguous id (see {@link resolveSceneById}).
 * Shared by every tool that addresses an existing scene to act on.
 */
export function resolveSceneHandle(
    luminary: Luminary,
    state: RootState,
    args: SceneHandleArgs,
): { sceneIndex: number; scene: Scene; sceneShortId: string } {
    const { scene_id } = args;
    if (scene_id == null || scene_id.trim() === '') {
        throw new SceneResolutionError(
            'Provide scene_id (the stable scene id from the video state) to identify the scene.',
        );
    }
    const resolved = resolveSceneById(luminary, state, scene_id);
    if ('error' in resolved) {
        throw new SceneResolutionError(resolved.error);
    }
    return { ...resolved, sceneShortId: sceneDisplayId(luminary, resolved.scene.id) };
}
